import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { environment } from "@infra-env/environment";
import type {
  CurrentUserProfileDto,
  CurrentUserProfileUpdateInput,
  ICurrentUserProfilePort,
} from "@core-ports/current-user-profile";

type UserByTokenApi = Record<string, unknown>;

function pickString(o: UserByTokenApi, key: string): string {
  const v = o[key];
  return typeof v === "string" ? v : "";
}

const PROFILE_PICTURE_MAX_BYTES = 2 * 1024 * 1024;
const PROFILE_PICTURE_ALLOWED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

type UploadIntentResponse = {
  objectKey: string;
  putUrl: string;
  requiredHeaders: Record<string, string>;
  expiresInSeconds: number;
};

@Injectable({
  providedIn: "root",
})
export class CurrentUserProfileApiService implements ICurrentUserProfilePort {
  private readonly basePath = `${environment.apiUrl}/user`;

  constructor(private readonly http: HttpClient) {}

  async getByToken(): Promise<CurrentUserProfileDto> {
    const raw = await firstValueFrom(
      this.http.get<UserByTokenApi>(`${this.basePath}/by-token`),
    );
    const id = pickString(raw, "id");
    const firstName = pickString(raw, "firstName");
    const lastName = pickString(raw, "lastName");
    const email = pickString(raw, "email");
    const role = pickString(raw, "role");
    const profilePicRaw = raw["profilePic"];
    const hasRaw = raw["hasProfilePicture"];
    return {
      id,
      firstName,
      lastName,
      email,
      role,
      profilePic:
        typeof profilePicRaw === "string" && profilePicRaw.trim() !== ""
          ? profilePicRaw
          : undefined,
      hasProfilePicture: hasRaw === true,
      phoneNumber: pickString(raw, "phoneNumber") || undefined,
      addressLegal: pickString(raw, "addressLegal") || undefined,
      birthDate: pickString(raw, "birthDate") || undefined,
      gender: pickString(raw, "gender") || undefined,
      nationality: pickString(raw, "nationality") || undefined,
      rut: pickString(raw, "rut") || undefined,
      profesion: pickString(raw, "profesion") || undefined,
      speciality: pickString(raw, "speciality") || undefined,
      registrationNumber: pickString(raw, "registrationNumber") || undefined,
      accountStatus: pickString(raw, "accountStatus") || undefined,
      destructiveDeleteEnabled: raw["destructiveDeleteEnabled"] === true,
    };
  }

  async updateMyProfile(data: CurrentUserProfileUpdateInput): Promise<CurrentUserProfileDto> {
    const me = await this.getByToken();
    await firstValueFrom(
      this.http.patch<unknown>(`${this.basePath}/${me.id}`, data),
    );
    return this.getByToken();
  }

  async uploadProfilePicture(file: File): Promise<void> {
    if (file.size > PROFILE_PICTURE_MAX_BYTES) {
      throw new Error("profile picture too large");
    }
    const mimeType = (file.type || "application/octet-stream").trim();
    if (!PROFILE_PICTURE_ALLOWED_MIME.has(mimeType)) {
      throw new Error("unsupported profile picture type");
    }
    const intent = await firstValueFrom(
      this.http.post<UploadIntentResponse>(
        `${this.basePath}/me/profile-picture/upload-intent`,
        {
          originalName: file.name,
          mimeType,
        },
      ),
    );
    const putHeaders = { ...intent.requiredHeaders };
    const putRes = await fetch(intent.putUrl, {
      method: "PUT",
      body: file,
      headers: putHeaders,
    });
    if (!putRes.ok) {
      throw new Error("S3 upload failed");
    }
    await firstValueFrom(
      this.http.post(`${this.basePath}/me/profile-picture/finalize`, {
        objectKey: intent.objectKey,
      }),
    );
  }

  async deleteProfilePicture(): Promise<void> {
    await firstValueFrom(
      this.http.delete(`${this.basePath}/me/profile-picture`),
    );
  }

  async getProfilePictureBlob(): Promise<Blob> {
    const me = await this.getByToken();
    const key = (me.profilePic ?? "").trim();
    if (key === "" || /^https?:\/\//i.test(key)) {
      throw new Error("no S3 avatar key");
    }
    const signed = await firstValueFrom(
      this.http.get<{ url: string }>(
        `${this.basePath}/me/profile-picture/signed-url`,
        {
          params: new HttpParams().set("objectKey", key),
        },
      ),
    );
    const r = await fetch(signed.url, { method: "GET" });
    if (!r.ok) {
      throw new Error("avatar download failed");
    }
    return r.blob();
  }

  async setDestructiveDeleteEnabled(enabled: boolean): Promise<boolean> {
    const raw = await firstValueFrom(
      this.http.patch<{ destructiveDeleteEnabled?: boolean }>(
        `${this.basePath}/me/destructive-delete`,
        { enabled },
      ),
    );
    return raw.destructiveDeleteEnabled === true;
  }
}
